// components/Auth.jsx - MOBILE RESPONSIVE VERSION

import { GoogleLogin } from "@react-oauth/google";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { handleGoogleLogin, handleCredentialLogin, handleGuestLogin } from "../../store/actions";

export function Auth() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("google"); // 'google' or 'credentials'
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");

  let user = useSelector((state) => state.user);
  const isLoading = user.loading;
  const reduxError = user.error ? user.errorMessage : "";

  useEffect(() => {
    if (user.loggedIn) {
      navigate("/dashboard/finance");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (reduxError) {
      setError(reduxError);
    }
  }, [reduxError]);

  const onGoogleSuccess = async (response) => {
    try {
      const token = response.credential;
      dispatch(handleGoogleLogin(token));
    } catch (error) {
      console.error("Google Login failed", error);
      setError("Google login failed. Please try again.");
    }
  };

  const onGoogleError = (error) => {
    console.error("Google Login failed", error);
    setError("Google login failed. Please try again.");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
  };

  const handleCredentialSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      if (!formData.email || !formData.password) {
        setError("Please fill in all fields");
        return;
      }
      await dispatch(handleCredentialLogin(formData));
    } catch (error) {
      console.error("Credential login failed", error);
      setError(error.message || "Login failed. Please check your credentials.");
    }
  };

  const handleGuestLoginClick = async () => {
    try {
      await dispatch(handleGuestLogin());
    } catch (error) {
      console.error("Guest login failed", error);
      setError("Failed to sign in as guest. Please try again.");
    }
  };

  return (
    <section className="h-screen flex flex-col md:flex-row overflow-hidden relative p-0 md:p-8">
      {/* Full Page Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
          <div className="flex flex-col items-center">
            <svg
              className="animate-spin h-10 w-10 sm:h-12 sm:w-12 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="mt-4 text-base sm:text-lg text-gray-700 font-medium">Signing In...</span>
          </div>
        </div>
      )}

      {/* Left Side - Pattern Background (Hidden on mobile, shown on desktop) */}
      <div className="hidden md:block md:w-1/2 h-48 md:h-full">
        <img
          src="/pattern.png"
          className="h-full w-full object-cover rounded-none md:rounded-3xl"
          alt="Authentication background pattern"
        />
      </div>
      
      {/* Right Side - Login Form */}
      <div className="w-full flex-1 md:flex-none md:w-1/2 flex items-center justify-center bg-white p-4 sm:p-6 md:p-8 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-3 sm:mb-4 md:mb-8">
            <img
              src="/pulse_logo.gif"
              alt="Pulse Logo"
              className="h-10 sm:h-12 md:h-16 w-auto"
            />
          </div>

          {/* Title */}
          <div className="text-center mb-3 sm:mb-4 md:mb-8">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1 md:hidden">
              Welcome Back
            </h1>
            <p className="text-sm sm:text-base text-gray-600">Sign in to your account</p>
          </div>

          {/* Tab Navigation - RESPONSIVE */}
          <div className="flex mb-3 sm:mb-4 md:mb-6 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab("google")}
              className={`flex-1 py-2 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                activeTab === "google"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <span className="hidden sm:inline">Google Sign In</span>
              <span className="sm:hidden">Google</span>
            </button>
            <button
              onClick={() => setActiveTab("credentials")}
              className={`flex-1 py-2 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                activeTab === "credentials"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <span className="hidden sm:inline">Email & Password</span>
              <span className="sm:hidden">Email</span>
            </button>
          </div>

          {/* Error Message - RESPONSIVE */}
          {error && (
            <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-xs sm:text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Google Login Tab - RESPONSIVE */}
          {activeTab === "google" && (
            <div className="space-y-3 sm:space-y-4">
              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={onGoogleSuccess}
                  onError={onGoogleError}
                  width="280"
                  size="large"
                  theme="outline"
                  text="signin_with"
                  shape="rectangular"
                />
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 text-center px-4">
                Sign in securely with your Google account
              </p>
            </div>
          )}

          {/* Credentials Login Tab - RESPONSIVE */}
          {activeTab === "credentials" && (
            <form onSubmit={handleCredentialSubmit} className="space-y-3 sm:space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    isLoading ? "bg-gray-50 cursor-not-allowed" : "bg-white"
                  }`}
                  placeholder="Enter your email"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                >
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    isLoading ? "bg-gray-50 cursor-not-allowed" : "bg-white"
                  }`}
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center py-2.5 sm:py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ${
                  isLoading
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg active:bg-blue-800"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Signing In...</span>
                  </div>
                ) : (
                  "Sign In"
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={(e) => e.preventDefault()}
                  className="text-xs sm:text-sm text-blue-600 hover:text-blue-500 transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          )}

          {/* Guest Login Option - RESPONSIVE */}
          <div className="mt-4 sm:mt-5 md:mt-8 text-center border-t border-gray-200 pt-3 sm:pt-4 md:pt-6">
            <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">
              or continue without signing in
            </p>
            <button
              onClick={handleGuestLoginClick}
              disabled={isLoading}
              className={`w-full sm:w-auto px-6 py-2.5 sm:py-2 rounded-md shadow text-sm font-medium transition-all duration-200 ${
                isLoading
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg active:bg-blue-800"
              }`}
            >
              Continue as Guest
            </button>
          </div>

          {/* Sign Up Link - RESPONSIVE */}
          <div className="mt-3 sm:mt-4 md:mt-6 text-center">
            <p className="text-xs text-gray-500">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={(e) => e.preventDefault()}
                className="text-blue-600 hover:text-blue-500 font-medium transition-colors"
              >
                Sign up here
              </button>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Auth;